import { type FC, useEffect, useState } from "react";
import { useShopfrontDispatch, useShopfrontSelector } from "../../_Bootstrap/hooks";
import { showDialog } from "../../Actions/Dialogs/DialogActions";
import { changeRegister } from "../../Actions/General/LocationActions";
import { CancelOptionButton } from "../../Common/Buttons/Button";
import PopupManager from "../../Common/Popups/PopupManager";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import { setHasJustTakenRegisterControl } from "../../Shopfront";
import { getOption } from "../../Utilities/Platform/Options/Options";
import Request, { expectPaymentRequired, PaymentRequiredError } from "../../Utilities/Platform/Request";
import { sendSocketMessage } from "../../Workers/Socket/SocketAgent";
import { SOCKET_EVENTS } from "../../Workers/Socket/SocketEvents";
import { sendSecurityEvent } from "../../Workers/Upload/UploadAgent";
import { LocationOutletSelector } from "./LocationOutletSelector";
import { LocationRegisterSelector } from "./LocationRegisterSelector";

enum LocationStage {
    OUTLET,
    REGISTER,
}

const LocationSelector: FC = () => {
    const presence = useShopfrontSelector(state => state.presence);
    const userName = useShopfrontSelector(state => state.user.id ? state.user.name : "");
    const userId = useShopfrontSelector(state => state.user.id);
    const isAdmin = useShopfrontSelector(state => !!(state.user.id && state.user.isAdmin));

    const dispatch = useShopfrontDispatch();
    const [ stage, setStage ] = useState(LocationStage.OUTLET);
    const [ outlet, setOutlet ] = useState<null | string>(null);
    const [ loading, setLoading ] = useState(false);
    const [ locations, setLocations ] = useState<Array<{
        name: string;
        id: string;
        registers: Array<{
            name: string;
            id: string;
        }>;
    }>>([]);
    let previousRegister: string | null = getOption("previous_register");

    if(previousRegister === "") {
        previousRegister = null;
    }

    useEffect(() => {
        let loadedServer: boolean | "FAILED" = false;
        let loadedLocal: false | typeof locations | "FAILED" = false;

        determineDataSource()
            .then(source => Promise.all([ source.outlets.all(), source.registers.all() ]))
            .then(([ outlets, registers ]) => {
                const formatted: typeof locations = outlets.map(outlet => ({
                    name     : outlet.name,
                    id       : outlet.uuid,
                    registers: [],
                }));

                for(let i = 0, l = registers.length; i < l; i++) {
                    for(let j = 0, n = formatted.length; j < n; j++) {
                        if(formatted[j].id === registers[i].outlet_id) {
                            formatted[j].registers.push({
                                name: registers[i].name,
                                id  : registers[i].uuid,
                            });

                            break;
                        }
                    }
                }

                loadedLocal = formatted;

                if(loadedServer === false || loadedServer === "FAILED") {
                    setLocations(formatted);
                }
            })
            .catch(err => {
                console.error("We were unable to load the locations from the local file", err);
                loadedLocal = "FAILED";

                if(loadedServer === "FAILED") {
                    PopupManager.error("We were unable to load the registers and outlets, putting you in global mode");
                    dispatch(changeRegister("global"));
                }
            });

        if(window && window.location && window.location.pathname === "/settings/account") {
            expectPaymentRequired();
        }

        Request.post<{
            data: {
                outlets: Array<{
                    id: string;
                    name: string;
                    registers: Array<{
                        id: string;
                        name: string;
                    }>;
                }>;
            };
        }>("/graphql", {
            query: `query GetOutlets {
                outlets {
                    id,
                    name,
                    registers {
                        id,
                        name,
                    },
                },
            }`,
        })
            .then(response => {
                loadedServer = true;
                const outlets = response.data.data.outlets;

                // This is to stop the location selector being stuck in a loop SHOPFRONT-5874
                determineDataSource()
                    .then(async source => {
                        for(const outlet of outlets) {
                            const hasOutlet = await source.outlets.get(outlet.id)
                                .then(() => true)
                                .catch(() => false);

                            if(!hasOutlet) {
                                await source.outlets.put({
                                    contact: {
                                        address_city    : "",
                                        address_country : "",
                                        address_postcode: "",
                                        address_state   : "",
                                        address_street_1: "",
                                        address_street_2: "",
                                        address_suburb  : "",
                                        email           : "",
                                        facebook        : "",
                                        fax             : "",
                                        mobile          : "",
                                        phone           : "",
                                        twitter         : "",
                                        uuid            : "",
                                        website         : "",
                                    },
                                    loyalty_enabled   : false,
                                    loyalty_name      : "",
                                    has_kitchen_screen: false,
                                    logo              : null,
                                    name              : outlet.name,
                                    business_number   : "",
                                    uuid              : outlet.id,
                                    settings          : {},
                                });
                            }

                            for(const register of outlet.registers) {
                                const hasRegister = await source.registers.get(register.id)
                                    .then(() => true)
                                    .catch(() => false);

                                if(!hasRegister) {
                                    await source.registers.put({
                                        uuid               : register.id,
                                        name               : register.name,
                                        settings           : {},
                                        payment_methods    : [],
                                        outlet_id          : outlet.id,
                                        surcharging_enabled: false,
                                        float              : {
                                            monday   : 0,
                                            tuesday  : 0,
                                            wednesday: 0,
                                            thursday : 0,
                                            friday   : 0,
                                            saturday : 0,
                                            sunday   : 0,
                                        },
                                        close_register_tags   : [],
                                        closure_print_settings: {
                                            account            : "none",
                                            account_payments   : "none",
                                            discounts          : "none",
                                            gift_cards_redeemed: "none",
                                            gift_cards_sold    : "none",
                                            movements          : "none",
                                            payments           : "none",
                                            refunds            : "none",
                                            tax                : "none",
                                            loyalty            : "none",
                                            notes              : false,
                                            tags               : false,
                                            payment_subtypes   : false,
                                            statistics         : false,
                                        },
                                    });
                                }
                            }
                        }
                    })
                    .catch(err => {
                        console.error("Unable to store the outlets in the local database", err);
                    });

                setLocations(outlets);
            })
            .catch(err => {
                if(err instanceof PaymentRequiredError) {
                    PopupManager.error(
                        "We've put you in global mode as your Vendor has expired"
                    );

                    dispatch(changeRegister("global"));

                    return;
                }

                console.error("Unable to load registers and outlets from the server", err);

                loadedServer = "FAILED";

                if(loadedLocal === "FAILED") {
                    PopupManager.error(
                        "We were unable to load the registers and outlets, we've put you in global mode"
                    );

                    dispatch(changeRegister("global"));
                } else if(loadedLocal) {
                    setLocations(loadedLocal);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [ dispatch, userId ]);

    useEffect(() => {
        if(locations.length === 1) {
            setOutlet(locations[0].id);
            setStage(LocationStage.REGISTER);
        }
    }, [ locations ]);

    return (
        <div
            className={
                "z-1 fixed inset-0 bg-linear-to-br from-stone-500 to-zinc-800 flex items-center " +
                "justify-center dark:from-stone-700 dark:to-zinc-900"
            }
        >
            <div className="max-h-screen w-full p-8 overflow-auto">
                <div className="bg-white rounded-lg shadow-2xl w-fit mx-auto p-8 pt-12 relative dark:bg-slate-600">
                    <h2 className="text-xl">Location Selector</h2>
                    <p>Please select which location best describes where your device is located.</p>
                    {loading && (
                        <div>
                            <div className="relative p-12">
                                <div
                                    className={
                                        "m-auto w-[40px] h-[40px] absolute text-center inset-0 animate-spin"
                                    }
                                >
                                    <div
                                        className={
                                            "w-4/6 h-4/6 inline-block absolute t-0 bg-gray-700 rounded-full " +
                                            "animate-ping"
                                        }
                                    />
                                    <div
                                        className={
                                            "w-4/6 h-4/6 inline-block absolute b-0 bg-gray-700 rounded-full " +
                                            "animate-ping"
                                        }
                                        style={{
                                            animationDelay: "-0.5s",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {!loading && stage === LocationStage.OUTLET && (
                        <LocationOutletSelector
                            outlets={locations}
                            previousRegister={previousRegister}
                            onOutletSelect={outlet => {
                                if(outlet === "global") {
                                    dispatch(changeRegister("global"));
                                    setTimeout(() => {
                                        void sendSecurityEvent("LOCATION_CHANGE", {});
                                    }, 100);

                                    return;
                                }

                                setOutlet(outlet);
                                setStage(LocationStage.REGISTER);
                            }}
                        />
                    )}
                    {!loading && stage === LocationStage.REGISTER && outlet && (
                        <LocationRegisterSelector
                            outletName={locations.filter(location => location.id === outlet)[0].name}
                            registers={locations.filter(location => location.id === outlet)[0].registers}
                            previous={previousRegister}
                            presence={
                                presence.map(pres => pres.register)
                                    .filter(register => typeof register === "string") as Array<string>
                            }
                            onBack={() => {
                                setOutlet(null);
                                setStage(LocationStage.OUTLET);
                            }}
                            onRegisterSelect={register => {
                                let outletName = "";
                                let registerName = "";

                                for(let i = 0, l = locations.length; i < l; i++) {
                                    if(locations[i].id !== outlet) {
                                        continue;
                                    }

                                    outletName = locations[i].name;

                                    if(register) {
                                        for(let j = 0, n = locations[i].registers.length; j < n; j++) {
                                            if(locations[i].registers[j].id === register) {
                                                registerName = locations[i].registers[j].name;
                                                break;
                                            }
                                        }
                                    }

                                    break;
                                }

                                const vars = [{
                                    id  : outlet,
                                    name: outletName,
                                }, typeof register === "undefined" ? undefined : {
                                    id  : register,
                                    name: registerName,
                                } ];

                                if(!isAdmin && register) {
                                    // Check if we have presence
                                    for(let i = 0, l = presence.length; i < l; i++) {
                                        if(presence[i].register === register) {
                                            dispatch(showDialog(
                                                "Register in Use",
                                                <p>
                                                    This register is currently in use, would you like to take
                                                    control of this register?
                                                </p>,
                                                "question",
                                                [ CancelOptionButton, {
                                                    label   : "Yes",
                                                    classes : [ "button-primary" ],
                                                    callback: () => {
                                                        setHasJustTakenRegisterControl();
                                                        dispatch(changeRegister(...vars));
                                                        sendSocketMessage(SOCKET_EVENTS.KICK_REGISTER, {
                                                            register: register,
                                                            user    : userName,
                                                        });
                                                        setTimeout(() => {
                                                            sendSecurityEvent("LOCATION_CHANGE", {});
                                                        }, 100);
                                                    },
                                                }],
                                                false,
                                                false
                                            ));

                                            return;
                                        }
                                    }
                                }

                                dispatch(changeRegister(...vars));
                                setTimeout(() => {
                                    sendSecurityEvent("LOCATION_CHANGE", {});
                                }, 100);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default LocationSelector;
